import React, { useState, useRef } from "react";
import { Box, Typography, Button, IconButton, styled } from "@mui/material";
import { SentimentButtons } from "../../left-panel/SentimentButtons";
import { FlashIcon } from "../../icons/FlashIcon";
import { BackArrowIcon } from "../../icons/BackArrowIcon";
import { SmallPerkCard } from "./SmallPerkCard";
import type {
  MSHatData,
  PerkQuirkSentimentRecord,
  HatSentiment,
} from "../../../types";

interface PerkQuirkCompletionItem {
  id: string;
  name: string;
  type: "perk" | "quirk";
}

interface PerkQuirkCompletionContentProps {
  hat: MSHatData;
  perkQuirkItems: PerkQuirkCompletionItem[];
  onContinue: () => void;
  xpValue: number;
  sentiments?: PerkQuirkSentimentRecord[];
  hatSentiment?: HatSentiment;
  onHatSentimentChange?: (sentiment: HatSentiment) => void;
}

const ContentContainer = styled(Box, {
  label: "PerkQuirkCompletionContentContainer",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(4),
  width: "100%",
  maxWidth: "800px",
}));

const SubTitle = styled(Typography, {
  label: "PerkQuirkCompletionSubTitle",
})(({ theme }) => ({
  fontFamily: "Proxima Nova",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "36px",
  color: "#ffffff",
  textAlign: "center",
  marginBottom: theme.spacing(2),
  "@media (max-width: 900px)": {
    fontSize: "20px",
    lineHeight: "30px",
  },
}));

const XPSection = styled(Box, {
  label: "PerkQuirkCompletionXPSection",
})(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: 0,
}));

const XPText = styled(Typography, {
  label: "PerkQuirkCompletionXPText",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#22C55E",
}));

const ButtonContainer = styled(Box, {
  label: "PerkQuirkCompletionButtonContainer",
})(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
  width: "100%",
  marginTop: theme.spacing(0),
}));

const ContinueButton = styled(Button, {
  label: "PerkQuirkCompletionContinueButton",
})(({ theme }) => ({
  backgroundColor: "#078dee",
  color: "#ffffff",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.spacing(1),
  minWidth: "120px",
  height: "48px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0570c4",
  },
}));

const MiniCarousel = styled(Box, {
  label: "PerkQuirkCompletionMiniCarousel",
})(() => ({
  position: "relative",
  overflow: "hidden",
  width: 800,
}));

const Scroller = styled("div", {
  label: "PerkQuirkCompletionScroller",
})(() => ({
  display: "inline-flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  gap: "45px",
  transition: "transform 0.3s ease",
}));

const NavButton = styled(IconButton, {
  label: "PerkQuirkCompletionNavButton",
})(() => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  color: "#1C252E",
  width: 40,
  height: 40,
  zIndex: 2,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 1)",
  },
  "&:disabled": {
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    color: "rgba(28, 37, 46, 0.3)",
  },
}));

const RatingSection = styled(Box, {
  label: "PerkQuirkCompletionRatingSection",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  width: "100%",
  backgroundColor: "#fff",
  borderRadius: 16,
  padding: theme.spacing(2),
}));

const RatingTitle = styled(Typography, {
  label: "PerkQuirkCompletionRatingTitle",
})(() => ({
  color: "#1C252E",
  textAlign: "center",
  fontFamily: "Proxima Nova",
  fontSize: "24px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "36px",
  letterSpacing: "0px",
}));

const LeftNavButton = styled(NavButton, {
  label: "PerkQuirkCompletionLeftNavButton",
})(() => ({
  left: -50,
  borderRadius: 16,
}));

const RightNavButton = styled(NavButton, {
  label: "PerkQuirkCompletionRightNavButton",
})(() => ({
  right: -50,
  borderRadius: 16,
}));

export const PerkQuirkCompletionContent = ({
  hat,
  perkQuirkItems,
  onContinue,
  xpValue,
  sentiments = [],
  hatSentiment,
  onHatSentimentChange,
}: PerkQuirkCompletionContentProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const itemWidth = 377;
  const gap = 45;
  const totalItemWidth = itemWidth + gap;

  const handleScrollLeft = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      if (scrollerRef.current) {
        scrollerRef.current.style.transform = `translateX(-${newIndex * totalItemWidth}px)`;
      }
    }
  };

  const handleScrollRight = () => {
    if (currentIndex < perkQuirkItems.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      if (scrollerRef.current) {
        scrollerRef.current.style.transform = `translateX(-${newIndex * totalItemWidth}px)`;
      }
    }
  };

  return (
    <ContentContainer>
      <SubTitle>
        You&apos;ve explored the perks and quirks of being a {hat.displayName}
      </SubTitle>

      <Box sx={{ position: "relative" }}>
        <LeftNavButton onClick={handleScrollLeft} disabled={currentIndex === 0}>
          <BackArrowIcon width={20} height={20} />
        </LeftNavButton>
        <RightNavButton
          onClick={handleScrollRight}
          disabled={currentIndex >= perkQuirkItems.length - 1}
        >
          <BackArrowIcon width={20} height={20} />
        </RightNavButton>
        <MiniCarousel>
          <Scroller ref={scrollerRef}>
            {perkQuirkItems.map((item) => (
              <SmallPerkCard
                key={item.id}
                item={item}
                sentiments={sentiments}
              />
            ))}
          </Scroller>
        </MiniCarousel>
      </Box>
      <RatingSection>
        <RatingTitle>
          {hatSentiment ? "Your Rating" : "Rate this Hat"}
        </RatingTitle>

        <SentimentButtons
          sentiment={hatSentiment}
          onChange={onHatSentimentChange}
        />
      </RatingSection>
      <XPSection>
        <FlashIcon width={24} color="#22C55E" />
        <XPText>You earned {xpValue} XP</XPText>
      </XPSection>
      <ButtonContainer>
        <ContinueButton onClick={onContinue}>Continue</ContinueButton>
      </ButtonContainer>
    </ContentContainer>
  );
};
